import React from "react";
import { motion } from "framer-motion";
import { X, ArrowLeft } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import MarkdownRenderer from "../Components/MarkdownRenderer";

export default function CookiePolicy({ onClose }) {
    const { t, language } = useLanguage();

    const content = {
        it: {
            title: "Cookie Policy",
            lastUpdated: "Ultimo aggiornamento: 16 Febbraio 2026",
            sections: [
                {
                    title: "1. Cosa sono i Cookie",
                    content: `I cookie sono piccoli file di testo che i siti web visitati inviano al browser dell'utente, dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva. I cookie possono essere utilizzati per diverse finalità: esecuzione di autenticazioni informatiche, monitoraggio di sessioni, memorizzazione di informazioni su specifiche configurazioni riguardanti gli utenti che accedono al server, facilitazione nella fruizione dei contenuti online.

I cookie possono rimanere nel sistema anche per lunghi periodi e possono contenere anche un codice identificativo unico.`
                },
                {
                    title: "2. Tipologie di Cookie Utilizzati",
                    content: `Il nostro sito utilizza diverse tipologie di cookie:

**Cookie Tecnici (Non richiedono consenso)**
Questi cookie sono essenziali per il corretto funzionamento del sito e per permetterti di navigare e utilizzare le sue funzionalità. Senza questi cookie, alcune parti del sito non funzionerebbero correttamente.

**Cookie Analitici (Richiedono consenso)**
Utilizziamo cookie analitici per raccogliere informazioni su come i visitatori utilizzano il nostro sito. Questi dati ci aiutano a migliorare il sito e l'esperienza dell'utente. Le informazioni raccolte sono aggregate e anonime.

**Cookie di Marketing (Richiedono consenso)**
Questi cookie vengono utilizzati per tracciare i visitatori sui siti web. L'intento è quello di visualizzare annunci pertinenti e coinvolgenti per il singolo utente.`
                },
                {
                    title: "3. Cookie Utilizzati nel Dettaglio",
                    content: `**Cookie Tecnici di Prima Parte:**

| Nome | Finalità | Durata |
|------|----------|--------|
| cookie_consent | Memorizza le preferenze dell'utente relative ai cookie | 12 mesi |
| session_id | Gestione della sessione utente | Sessione |
| lang_pref | Memorizza la preferenza di lingua | 12 mesi |

**Cookie Analitici (previo consenso):**

| Nome | Fornitore | Finalità | Durata |
|------|-----------|----------|--------|
| _ga | Google Analytics | Distinguere gli utenti | 24 mesi |
| _gid | Google Analytics | Distinguere gli utenti | 24 ore |
| _gat | Google Analytics | Limitare le richieste | 1 minuto |

**Cookie di Marketing (previo consenso):**

Al momento non utilizziamo cookie di marketing di terze parti. Ci riserviamo la facoltà di attivare tali cookie in futuro, previa tua ulteriore manifestazione di consenso.`
                },
                {
                    title: "4. Cookie di Terze Parti",
                    content: `Il nostro sito può contenere collegamenti a siti web di terze parti. Non abbiamo alcun controllo sui cookie che possono essere utilizzati da terze parti e ti invitiamo a consultare le rispettive informative privacy e cookie policy.

**Google Analytics**
Utilizziamo Google Analytics per analizzare l'utilizzo del nostro sito. Google Analytics è un servizio di analisi web fornito da Google LLC. Per maggiori informazioni su come Google utilizza i dati raccolti attraverso il nostro sito, visita: https://policies.google.com/privacy

**Embedded Content**
Il nostro sito può contenere video incorporati da YouTube. Questi servizi possono raccogliere dati sull'utilizzo e impostare cookie. Consigliamo di consultare le relative privacy policy.`
                },
                {
                    title: "5. Come Gestire i Cookie",
                    content: `Hai il diritto di decidere se accettare o rifiutare i cookie. Puoi esercitare le tue preferenze sui cookie attraverso:

**a) Banner Cookie**
Alla prima visita del sito, ti viene mostrato un banner che ti permette di:
- Accettare tutti i cookie
- Rifiutare i cookie non essenziali
- Personalizzare le tue preferenze

**b) Impostazioni del Browser**
Puoi configurare il tuo browser per accettare o rifiutare automaticamente i cookie. Ecco le guide per i browser più comuni:

- **Chrome:** Menu → Impostazioni → Privacy e sicurezza → Cookie e altri dati dei siti
- **Firefox:** Menu → Opzioni → Privacy e sicurezza → Cookie e dati dei siti web
- **Safari:** Preferenze → Privacy → Gestisci dati siti web
- **Edge:** Menu → Impostazioni → Cookie e autorizzazioni sito → Cookie e dati dei siti

**c) Strumenti di terze parti**
Puoi anche utilizzare strumenti forniti da terze parti per gestire i cookie, come:
- Your Online Choices: www.youronlinechoices.eu
- Network Advertising Initiative: www.networkadvertising.org/choices

**Attenzione:** Bloccando tutti i cookie, alcune funzionalità del sito potrebbero non essere disponibili.`
                },
                {
                    title: "6. Cookie Flash",
                    content: `Il nostro sito non utilizza Adobe Flash Player né cookie Flash (Local Shared Objects).`
                },
                {
                    title: "7. Web Beacon e Pixel Tag",
                    content: `Attualmente non utilizziamo web beacon o pixel tag per il tracciamento. Qualora decidessimo di utilizzare tali tecnologie in futuro, questa policy sarà aggiornata di conseguenza.`
                },
                {
                    title: "8. Modifiche alla Cookie Policy",
                    content: `Questa Cookie Policy può essere modificata nel tempo, anche in conseguenza di modifiche normative. Ti invitiamo a consultare periodicamente questa pagina per rimanere aggiornato sulle nostre pratiche relative ai cookie.

La data di ultimo aggiornamento è indicata in alto nella pagina.`
                },
                {
                    title: "9. Ulteriori Informazioni",
                    content: `Per ulteriori informazioni sul trattamento dei tuoi dati personali, consulta la nostra Privacy Policy.

Per domande specifiche sui cookie utilizzati dal nostro sito, puoi contattarci:

**ELLEGI S.r.l.**
Viale Vittorio Veneto, 6
20124 Milano (MI), Italia
Email: comm@lg-mi.it
Tel: +39 02 2895741`
                }
            ]
        },
        en: {
            title: "Cookie Policy",
            lastUpdated: "Last updated: February 16, 2026",
            sections: [
                {
                    title: "1. What are Cookies",
                    content: `Cookies are small text files that websites send to the user's browser, where they are stored to be retransmitted to the same sites upon subsequent visits. Cookies can be used for various purposes: performing computer authentication, monitoring sessions, storing information about specific configurations regarding users accessing the server, facilitating online content consumption.

Cookies may remain in the system for long periods and may contain a unique identification code.`
                },
                {
                    title: "2. Types of Cookies Used",
                    content: `Our website uses different types of cookies:

**Technical Cookies (Do not require consent)**
These cookies are essential for the proper functioning of the site and to allow you to navigate and use its features. Without these cookies, some parts of the site would not work properly.

**Analytical Cookies (Require consent)**
We use analytical cookies to collect information about how visitors use our site. This data helps us improve the site and user experience. The information collected is aggregated and anonymous.

**Marketing Cookies (Require consent)**
These cookies are used to track visitors across websites. The intent is to display ads that are relevant and engaging for the individual user.`
                },
                {
                    title: "3. Cookies Used in Detail",
                    content: `**First-Party Technical Cookies:**

| Name | Purpose | Duration |
|------|---------|----------|
| cookie_consent | Stores user cookie preferences | 12 months |
| session_id | User session management | Session |
| lang_pref | Stores language preference | 12 months |

**Analytical Cookies (with consent):**

| Name | Provider | Purpose | Duration |
|------|----------|---------|----------|
| _ga | Google Analytics | Distinguish users | 24 months |
| _gid | Google Analytics | Distinguish users | 24 hours |
| _gat | Google Analytics | Limit requests | 1 minute |

**Marketing Cookies (with consent):**

We currently do not use third-party marketing cookies. We reserve the right to activate such cookies in the future, subject to your further consent.`
                },
                {
                    title: "4. Third-Party Cookies",
                    content: `Our site may contain links to third-party websites. We have no control over cookies that may be used by third parties and we invite you to consult their respective privacy and cookie policies.

**Google Analytics**
We use Google Analytics to analyze the use of our site. Google Analytics is a web analytics service provided by Google LLC. For more information on how Google uses data collected through our site, visit: https://policies.google.com/privacy

**Embedded Content**
Our site may contain embedded videos from YouTube. These services may collect usage data and set cookies. We recommend consulting their respective privacy policies.`
                },
                {
                    title: "5. How to Manage Cookies",
                    content: `You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences through:

**a) Cookie Banner**
On your first visit to the site, you are shown a banner that allows you to:
- Accept all cookies
- Reject non-essential cookies
- Customize your preferences

**b) Browser Settings**
You can configure your browser to automatically accept or reject cookies. Here are guides for the most common browsers:

- **Chrome:** Menu → Settings → Privacy and security → Cookies and other site data
- **Firefox:** Menu → Options → Privacy and security → Cookies and site data
- **Safari:** Preferences → Privacy → Manage Website Data
- **Edge:** Menu → Settings → Cookies and site permissions → Cookies and site data

**c) Third-party tools**
You can also use tools provided by third parties to manage cookies, such as:
- Your Online Choices: www.youronlinechoices.eu
- Network Advertising Initiative: www.networkadvertising.org/choices

**Warning:** Blocking all cookies may make some site features unavailable.`
                },
                {
                    title: "6. Flash Cookies",
                    content: `Our site does not use Adobe Flash Player or Flash cookies (Local Shared Objects).`
                },
                {
                    title: "7. Web Beacons and Pixel Tags",
                    content: `We currently do not use web beacons or pixel tags for tracking. Should we decide to use such technologies in the future, this policy will be updated accordingly.`
                },
                {
                    title: "8. Changes to Cookie Policy",
                    content: `This Cookie Policy may be modified over time, including as a result of regulatory changes. We invite you to periodically consult this page to stay updated on our cookie practices.

The last update date is indicated at the top of the page.`
                },
                {
                    title: "9. Additional Information",
                    content: `For more information on the processing of your personal data, please consult our Privacy Policy.

For specific questions about the cookies used by our site, you can contact us:

**ELLEGI S.r.l.**
Viale Vittorio Veneto, 6
20124 Milan (MI), Italy
Email: comm@lg-mi.it
Tel: +39 02 2895741`
                }
            ]
        }
    };

    const currentContent = language === 'it' ? content.it : content.en;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[--bg-primary] overflow-y-auto"
        >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[--bg-primary]/95 backdrop-blur-sm border-b border-[--border-color]">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-[--text-secondary] hover:text-[--accent] transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>{language === 'it' ? 'Torna alla Home' : 'Back to Home'}</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-[--accent]/20 flex items-center justify-center hover:bg-[--accent] transition-colors group"
                    >
                        <X className="w-5 h-5 text-[--accent] group-hover:text-black transition-colors" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <h1 className="text-4xl md:text-5xl font-bold text-[--accent] mb-2">
                        {currentContent.title}
                    </h1>
                    <p className="text-sm text-[--text-secondary] mb-12">
                        {currentContent.lastUpdated}
                    </p>

                    <div className="space-y-8">
                        {currentContent.sections.map((section, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 * (index + 1) }}
                                className="bg-[--bg-secondary] p-6 rounded-lg border border-[--border-color]"
                            >
                                <h2 className="text-xl font-bold text-[--text-primary] mb-4">
                                    {section.title}
                                </h2>
                                <MarkdownRenderer content={section.content} />
                            </motion.div>
                        ))}
                    </div>

                    {/* Footer CTA */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="mt-12 p-6 bg-[--accent]/10 border border-[--accent]/20 rounded-lg text-center"
                    >
                        <p className="text-[--text-primary] mb-4">
                            {language === 'it'
                                ? 'Hai domande sui cookie che utilizziamo?'
                                : 'Do you have questions about the cookies we use?'}
                        </p>
                        <a
                            href="mailto:comm@lg-mi.it"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[--accent] hover:bg-[--accent-hover] text-black font-semibold rounded-lg transition-colors"
                        >
                            {language === 'it' ? 'Contattaci' : 'Contact Us'}
                        </a>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
}
